import { Link } from "wouter";
import { Printer, ArrowLeft, ShieldAlert } from "lucide-react";

export default function IncidentResponse() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 print:bg-white print:text-black">

      {/* Screen-only toolbar */}
      <div className="print:hidden sticky top-0 z-50 bg-slate-900 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/agent/dashboard">
              <span className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors cursor-pointer">
                <ArrowLeft className="w-4 h-4" /> Dashboard
              </span>
            </Link>
            <span className="text-slate-700">|</span>
            <span className="flex items-center gap-2 text-sm font-medium text-orange-400">
              <ShieldAlert className="w-4 h-4" /> Internal Document — Do Not Distribute
            </span>
          </div>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 text-sm bg-white text-slate-900 hover:bg-slate-100 px-4 py-1.5 rounded-md font-medium transition-colors"
          >
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Document */}
      <div className="max-w-4xl mx-auto px-8 py-10 print:px-0 print:py-0 print:max-w-none">
        <div className="bg-slate-900 print:bg-white border border-slate-800 print:border-0 rounded-xl print:rounded-none p-10 print:p-0">

          {/* Header */}
          <div className="border-b-2 border-slate-700 print:border-slate-300 pb-6 mb-8 print:pb-4 print:mb-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold tracking-[0.25em] text-slate-500 print:text-slate-400 uppercase mb-2">
                  Vexis Talent Agency — Internal Compliance Document
                </p>
                <h1 className="text-2xl font-bold text-white print:text-black mb-1">
                  Data Breach Incident Response Plan
                </h1>
                <p className="text-sm text-slate-400 print:text-slate-500">
                  LHA Holdings Ltd trading as Vexis Talent Agency &nbsp;·&nbsp; Auckland, New Zealand
                </p>
              </div>
              <div className="text-right text-xs text-slate-500 print:text-slate-400 space-y-1 flex-shrink-0 ml-6">
                <p>Version 1.0</p>
                <p>April 2026</p>
                <p className="font-bold text-orange-500 print:text-orange-700">CONFIDENTIAL</p>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-7 print:space-y-5">

            <Section number="1" title="Detection" timeframe="Ongoing">
              <p>Monitor the following sources daily:</p>
              <ul className="mt-2 space-y-1">
                <BulletItem>Supabase audit logs — flag any access outside of expected agent/system accounts</BulletItem>
                <BulletItem>Cloudflare access logs — flag unusual geographic patterns or volume spikes</BulletItem>
                <BulletItem>Sentry error alerts — flag repeated 4xx/5xx patterns or unusual API usage</BulletItem>
              </ul>
              <AlertBox>
                Any confirmed or suspected unauthorised access triggers immediate alert to{" "}
                <strong>compliance@vexistalentagency.com</strong> and initiates this plan.
              </AlertBox>
            </Section>

            <Section number="2" title="Assessment" timeframe="Within 4 hours of detection">
              <p>Document answers to the following before escalating:</p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 print:grid-cols-2 gap-3">
                {[
                  ["Candidates affected", "Which individuals, by name and candidate ID"],
                  ["Data accessed", "Video, transcript, scores, contact details — specify"],
                  ["Sub-processor involved", "Supabase, Cloudflare, Replit, or connected ATS provider"],
                  ["Access vector", "Compromised credential, API key, or application vulnerability"],
                ].map(([label, detail]) => (
                  <div key={label} className="bg-slate-800 print:bg-slate-100 rounded-lg p-3 text-sm">
                    <p className="font-semibold text-white print:text-black">{label}</p>
                    <p className="text-slate-400 print:text-slate-600 text-xs mt-0.5">{detail}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section number="3" title="Containment" timeframe="Within 4 hours of detection">
              <ol className="space-y-2 list-none">
                {[
                  "Revoke all active ATS OAuth tokens via your integration provider's revocation endpoint",
                  "Rotate the Cloudflare Stream API token in the Cloudflare dashboard",
                  "Change the Supabase database password and cycle all connection strings",
                  "Disable the affected integration endpoints in code until patched and redeployed",
                ].map((step, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-300 print:text-slate-700">
                    <span className="font-bold text-orange-400 print:text-orange-700 w-5 flex-shrink-0">{i + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </Section>

            <Section number="4" title="Notification — GDPR (EU/UK)" timeframe="Within 72 hours of detection">
              <p>If any affected candidate is based in the EU or UK:</p>
              <ul className="mt-2 space-y-1">
                <BulletItem>Notify the relevant supervisory authority within <strong>72 hours</strong> of becoming aware of the breach</BulletItem>
                <BulletItem>Notify affected candidates within <strong>72 hours</strong> — include: what happened, which data was involved, and what steps have been taken</BulletItem>
              </ul>
            </Section>

            <Section number="5" title="Notification — ATS Provider" timeframe="Within 24 hours of confirmed breach">
              <p>
                Email your ATS provider's API support contact with the following:
              </p>
              <ul className="mt-2 space-y-1">
                <BulletItem>Nature and timeline of the incident</BulletItem>
                <BulletItem>Data categories affected (candidate IDs, assessment data, OAuth tokens)</BulletItem>
                <BulletItem>Containment steps taken</BulletItem>
                <BulletItem>ATS candidate IDs affected, if known</BulletItem>
              </ul>
            </Section>

            <Section number="6" title="Notification — Other Jurisdictions">
              <div className="grid grid-cols-1 sm:grid-cols-3 print:grid-cols-3 gap-3 mt-2">
                {[
                  {
                    region: "New Zealand",
                    law: "Privacy Act 2020",
                    action: "Notify the Office of the Privacy Commissioner if the breach is likely to cause serious harm.",
                    contact: "www.privacy.org.nz",
                  },
                  {
                    region: "Australia",
                    law: "NDB Scheme",
                    action: "Notify the OAIC and affected individuals if the breach is likely to result in serious harm.",
                    contact: "www.oaic.gov.au",
                  },
                  {
                    region: "Canada",
                    law: "PIPEDA",
                    action: "Notify the OPC and affected individuals if there is significant harm risk.",
                    contact: "www.priv.gc.ca",
                  },
                ].map((j) => (
                  <div key={j.region} className="bg-slate-800 print:bg-slate-100 rounded-lg p-3 text-sm">
                    <p className="font-bold text-white print:text-black">{j.region}</p>
                    <p className="text-xs text-slate-500 mb-1">{j.law}</p>
                    <p className="text-slate-300 print:text-slate-700 text-xs">{j.action}</p>
                    <p className="text-xs text-slate-500 mt-1 font-mono">{j.contact}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section number="7" title="Post-Incident Review" timeframe="Within 7 days">
              <ol className="space-y-2 list-none">
                {[
                  "Document root cause analysis — how did the breach occur and what failed",
                  "Update security controls, API key rotation schedules, and access policies accordingly",
                  "Add a regression test case to the sandbox testing suite covering the identified vector",
                ].map((step, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-300 print:text-slate-700">
                    <span className="font-bold text-slate-500 w-5 flex-shrink-0">{i + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </Section>

            <Section number="8" title="Key Contacts">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-slate-700 print:border-slate-300">
                      <Th>Role</Th>
                      <Th>Name / Channel</Th>
                      <Th>Contact</Th>
                    </tr>
                  </thead>
                  <tbody>
                    <ContactRow role="Primary (Compliance)" name="Vexis Compliance" contact="compliance@vexistalentagency.com" />
                    <ContactRow role="Technical Lead" name="Lead Developer" contact="[assign on onboarding]" />
                    <ContactRow role="Legal Counsel (NZ)" name="NZ Solicitor" contact="[add NZ solicitor details]" />
                    <ContactRow role="ATS Partner" name="Partner API Support" contact="[see ATS provider integration docs]" />
                    <ContactRow role="Privacy Commissioner (NZ)" name="OPC" contact="www.privacy.org.nz" />
                  </tbody>
                </table>
              </div>
            </Section>

          </div>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-slate-800 print:border-slate-300 flex flex-col sm:flex-row justify-between items-start gap-2 text-xs text-slate-600 print:text-slate-400">
            <p>Vexis Talent Agency — Data Breach Incident Response Plan v1.0</p>
            <p>Last reviewed: April 2026 &nbsp;·&nbsp; Next review due: April 2027</p>
          </div>

        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 18mm 16mm;
          }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function Section({ number, title, timeframe, children }: {
  number: string;
  title: string;
  timeframe?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="group">
      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-xs font-bold text-slate-600 print:text-slate-400 w-5 flex-shrink-0">{number}.</span>
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="text-base font-bold text-white print:text-black">{title}</h2>
          {timeframe && (
            <span className="text-xs font-semibold text-orange-400 print:text-orange-700 bg-orange-400/10 print:bg-transparent px-2 py-0.5 rounded-full print:px-0 print:py-0">
              {timeframe}
            </span>
          )}
        </div>
      </div>
      <div className="ml-8 text-sm text-slate-300 print:text-slate-700 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function BulletItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-sm text-slate-300 print:text-slate-700">
      <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-500 flex-shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function AlertBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-3 border-l-4 border-orange-500 pl-4 py-2 bg-orange-500/5 print:bg-transparent text-sm text-slate-300 print:text-slate-700">
      {children}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-500 print:text-slate-400 uppercase tracking-wider">
      {children}
    </th>
  );
}

function ContactRow({ role, name, contact }: { role: string; name: string; contact: string }) {
  return (
    <tr className="border-b border-slate-800 print:border-slate-200">
      <td className="py-2 pr-4 text-slate-400 print:text-slate-600">{role}</td>
      <td className="py-2 pr-4 text-white print:text-black font-medium">{name}</td>
      <td className="py-2 text-slate-400 print:text-slate-600 font-mono text-xs">{contact}</td>
    </tr>
  );
}
